/** Gemeinsamer Zustand: Wer schaut gerade, welcher Bestand ist geladen,
 *  welche Meldung steht an.
 *
 *  Die Nutzerwahl liegt in localStorage — kein Login, aber die Entscheidung
 *  bleibt über Sitzungen erhalten (Kernanforderung).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Book, BookList, Owner, SeriesInfo } from "../../../shared/types";
import * as api from "./api";

const STORAGE_KEY = "buecherregal.wer";

export interface Meldung {
  id: number;
  text: string;
  art: "ok" | "warnung" | "fehler";
}

interface Store {
  owner: Owner;
  setOwner: (o: Owner) => void;
  books: Book[];
  lists: BookList[];
  series: SeriesInfo[];
  laedt: boolean;
  fehler: string | null;
  modus: api.ApiMode;
  ki: boolean;
  neuLaden: () => Promise<void>;
  /** Ein Buch lokal ersetzen, ohne den ganzen Bestand neu zu holen. */
  ersetze: (book: Book) => void;
  melde: (text: string, art?: Meldung["art"]) => void;
  meldungen: Meldung[];
  schliesse: (id: number) => void;
  /** Führt einen schreibenden Aufruf aus und meldet das Ergebnis einheitlich. */
  schreibe: <T>(
    fn: () => Promise<api.Result<T>>,
    erfolg?: string,
  ) => Promise<T | null>;
}

const Ctx = createContext<Store | null>(null);

function gespeicherterOwner(): Owner {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "sarah" || v === "mario") return v;
  } catch {
    /* privater Modus o. Ä. — Standard nehmen */
  }
  return "sarah";
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [owner, setOwnerState] = useState<Owner>(gespeicherterOwner);
  const [books, setBooks] = useState<Book[]>([]);
  const [lists, setLists] = useState<BookList[]>([]);
  const [series, setSeries] = useState<SeriesInfo[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [modus, setModus] = useState<api.ApiMode>("unbekannt");
  const [ki, setKi] = useState(false);
  const [meldungen, setMeldungen] = useState<Meldung[]>([]);
  const zaehler = useRef(0);

  const melde = useCallback((text: string, art: Meldung["art"] = "ok") => {
    const id = ++zaehler.current;
    setMeldungen((m) => [...m, { id, text, art }]);
    // Fehler bleiben stehen, bis sie weggeklickt werden — sie sind wichtig
    if (art !== "fehler")
      setTimeout(() => setMeldungen((m) => m.filter((x) => x.id !== id)), 4200);
  }, []);

  const schliesse = useCallback((id: number) => {
    setMeldungen((m) => m.filter((x) => x.id !== id));
  }, []);

  const laden = useCallback(async (who: Owner) => {
    setLaedt(true);
    setFehler(null);
    try {
      const payload = await api.library(who);
      setBooks(payload.books);
      setLists(payload.lists);
      setSeries(payload.series);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
      setBooks([]);
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      const m = await api.connect();
      if (abgebrochen) return;
      setModus(m);
      setKi(api.kiVerfuegbar());
      await laden(owner);
    })();
    return () => {
      abgebrochen = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOwner = useCallback(
    (o: Owner) => {
      setOwnerState(o);
      try {
        localStorage.setItem(STORAGE_KEY, o);
      } catch {
        /* nicht schlimm — dann eben nur für diese Sitzung */
      }
      void laden(o);
    },
    [laden],
  );

  const neuLaden = useCallback(() => laden(owner), [laden, owner]);

  const ersetze = useCallback((book: Book) => {
    setBooks((alle) =>
      alle.map((b) => (b.id === book.id ? { ...b, ...book } : b)),
    );
  }, []);

  const schreibe = useCallback(
    async <T,>(
      fn: () => Promise<api.Result<T>>,
      erfolg?: string,
    ): Promise<T | null> => {
      try {
        const res = await fn();
        const text = res.message || erfolg;
        if (text) melde(text, res.status === "warnung" ? "warnung" : "ok");
        return res.data;
      } catch (err) {
        melde(err instanceof Error ? err.message : String(err), "fehler");
        return null;
      }
    },
    [melde],
  );

  const value = useMemo<Store>(
    () => ({
      owner,
      setOwner,
      books,
      lists,
      series,
      laedt,
      fehler,
      modus,
      ki,
      neuLaden,
      ersetze,
      melde,
      meldungen,
      schliesse,
      schreibe,
    }),
    [
      owner,
      setOwner,
      books,
      lists,
      series,
      laedt,
      fehler,
      modus,
      ki,
      neuLaden,
      ersetze,
      melde,
      meldungen,
      schliesse,
      schreibe,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore außerhalb des StoreProvider benutzt.");
  return ctx;
}
